import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Award,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Map as MapIcon,
  Menu,
  PartyPopper,
  Sparkles,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import grade3Data from '../data/grade3/grade3_assignments.v2.json';
import type { Grade3Activity, Grade3Lesson, Grade3Workbook } from '../types/grade3Workbook';
import { QACard, type QAQuestion } from '../components/assignments/QACard';
import { MatchingCard } from '../components/assignments/MatchingCard';
import { FillBlankCard, type FillBlankItem } from '../components/assignments/FillBlankCard';
import { InfoCard } from '../components/assignments/InfoCard';
import { ComingSoonCard } from '../components/assignments/ComingSoonCard';
import {
  RewriteCard,
  type RewriteExampleBlock,
  type RewriteItemView,
} from '../components/assignments/RewriteCard';
import {
  ConvertWordsCard,
  type ConvertExample,
  type ConvertItemView,
} from '../components/assignments/ConvertWordsCard';
import {
  ConvertWordsChoiceCard,
  type ConvertChoiceExample,
  type ConvertChoiceItem,
} from '../components/assignments/ConvertWordsChoiceCard';
import { ShortWriteCard } from '../components/assignments/ShortWriteCard';
import { RiddleChoiceCard } from '../components/assignments/RiddleChoiceCard';
import { ImageLabelCard, type ImageLabelItem } from '../components/assignments/ImageLabelCard';
import { MapLabelQuizCard, type MapLabelQuizItem } from '../components/assignments/MapLabelQuizCard';
import {
  CaseSuffixFillCard,
  type CaseSuffixFillExample,
  type CaseSuffixFillItem,
} from '../components/assignments/CaseSuffixFillCard';
import {
  readActivityScores,
  readWorkbookJson,
  TKM_G3_TOC_COMPLETION_KEY,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from '../components/assignments/workbookPersist';

const workbook = grade3Data as Grade3Workbook;
const TOC_LAST_KEY = 'tkm-g3-toc-last-v1';
const RESUME_DISMISS_SESSION_KEY = 'tkm-g3-toc-resume-dismissed';

type FlatRow = {
  lesson: Grade3Lesson;
  lessonIdx: number;
  activityIdx: number;
  act: Grade3Activity;
  persistenceId: string;
};

const FLAT_ROWS: FlatRow[] = (() => {
  const out: FlatRow[] = [];
  workbook.lessons.forEach((lesson, lessonIdx) => {
    for (let activityIdx = 0; activityIdx < lesson.activities.length; activityIdx++) {
      const act = lesson.activities[activityIdx];
      const sub = act.sub_activity != null ? String(act.sub_activity) : 'x';
      const persistenceId = `G${workbook.grade}-L${lesson.lesson_no}-A${act.activity_no}-${sub}-${act.ui_type}`;
      out.push({ lesson, lessonIdx, activityIdx, act, persistenceId });
    }
  });
  return out;
})();

const LESSON_ACTIVITY_TO_FLAT_INDEX = new Map<string, number>();
FLAT_ROWS.forEach((r, i) => {
  LESSON_ACTIVITY_TO_FLAT_INDEX.set(`${r.lesson.lesson_no}-${r.activityIdx}`, i);
});

function readStoredLinearIndex(): number {
  const last = readWorkbookJson<{ linearIndex: number } | null>(TOC_LAST_KEY, null);
  if (
    typeof last?.linearIndex === 'number' &&
    last.linearIndex >= 0 &&
    last.linearIndex < FLAT_ROWS.length
  ) {
    return last.linearIndex;
  }
  return 0;
}

const INITIAL_LINEAR_INDEX = readStoredLinearIndex();

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.map(String) : [];
}

function asPairArray(v: unknown): { left: string; right: string }[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((p) => {
      if (p && typeof p === 'object' && 'left' in p && 'right' in p) {
        return { left: String((p as { left: unknown }).left), right: String((p as { right: unknown }).right) };
      }
      return null;
    })
    .filter((x): x is { left: string; right: string } => x != null);
}

function asQuestionArray(v: unknown): QAQuestion[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((q) => {
      if (!q || typeof q !== 'object' || !('q_no' in q) || !('text' in q)) return null;
      const o = q as Record<string, unknown>;
      const q_no = Number(o.q_no);
      if (!Number.isFinite(q_no)) return null;
      const text = String(o.text);
      const out: QAQuestion = { q_no, text };
      if (Array.isArray(o.accepted_answers)) {
        const arr = o.accepted_answers.map(String).filter((s) => s.trim().length > 0);
        if (arr.length) out.accepted_answers = arr;
      }
      if (typeof o.hint === 'string' && o.hint.trim()) out.hint = o.hint.trim();
      if (typeof o.explanation === 'string' && o.explanation.trim()) out.explanation = o.explanation.trim();
      return out;
    })
    .filter((x): x is QAQuestion => x != null);
}

function normImageLabel(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return t;
}

function asImageLabelItems(v: unknown): ImageLabelItem[] {
  if (!Array.isArray(v)) return [];
  const out: ImageLabelItem[] = [];
  let seq = 0;
  for (const row of v) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    seq += 1;
    const idRaw = o.id;
    const idNum = typeof idRaw === 'number' && Number.isFinite(idRaw) ? idRaw : Number(idRaw);
    const id = Number.isFinite(idNum) ? idNum : seq;
    const image = typeof o.image === 'string' ? o.image : '';
    const answer = typeof o.answer === 'string' ? o.answer : '';
    let options = Array.isArray(o.options) ? o.options.map(String).filter((s) => s.length > 0) : [];
    const ansNorm = answer ? normImageLabel(answer) : '';
    if (answer && !options.some((x) => normImageLabel(x) === ansNorm)) {
      options = [answer, ...options];
    }
    if (options.length === 0 && answer) options = [answer];
    out.push({ id, image, answer, options });
  }
  return out;
}

function normMapLabel(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  return t;
}

function asMapLabelQuizItems(v: unknown): MapLabelQuizItem[] {
  if (!Array.isArray(v)) return [];
  const out: MapLabelQuizItem[] = [];
  let seq = 0;
  for (const row of v) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    seq += 1;
    const idRaw = o.id;
    const idNum = typeof idRaw === 'number' && Number.isFinite(idRaw) ? idRaw : Number(idRaw);
    const id = Number.isFinite(idNum) ? idNum : seq;
    const answer = typeof o.answer === 'string' ? o.answer : '';
    let options = Array.isArray(o.options) ? o.options.map(String).filter((s) => s.length > 0) : [];
    const ansNorm = answer ? normMapLabel(answer) : '';
    if (answer && !options.some((x) => normMapLabel(x) === ansNorm)) {
      options = [answer, ...options];
    }
    const hs = o.hotspot;
    if (!hs || typeof hs !== 'object') continue;
    const hx = Number((hs as { x?: unknown }).x);
    const hy = Number((hs as { y?: unknown }).y);
    if (!Number.isFinite(hx) || !Number.isFinite(hy)) continue;
    const x = Math.min(100, Math.max(0, hx));
    const y = Math.min(100, Math.max(0, hy));
    if (options.length === 0 && answer) options = [answer];
    if (!answer.trim() || options.length < 1) continue;
    out.push({ id, answer, options, hotspot: { x, y } });
  }
  return out.sort((a, b) => a.id - b.id);
}

function asFillItems(v: unknown): FillBlankItem[] {
  if (!Array.isArray(v)) return [];
  const out: FillBlankItem[] = [];
  for (const it of v) {
    if (!it || typeof it !== 'object' || !('q_no' in it) || !('template' in it)) continue;
    const row = it as { q_no: unknown; template: unknown; correct?: unknown };
    const q_no = Number(row.q_no);
    if (!Number.isFinite(q_no)) continue;
    const template = String(row.template);
    const correctRaw = row.correct;
    if (correctRaw != null && String(correctRaw).trim() !== '') {
      out.push({ q_no, template, correct: String(correctRaw) });
    } else {
      out.push({ q_no, template });
    }
  }
  return out;
}

function parseRewriteExample(act: Grade3Activity): { heading?: string; lines: string[] } | null {
  const ex = act.example as { heading?: unknown; lines?: unknown } | undefined;
  if (!ex || !Array.isArray(ex.lines)) return null;
  const lines = ex.lines.map(String);
  if (!lines.some((s) => s.trim())) return null;
  return { heading: typeof ex.heading === 'string' ? ex.heading : undefined, lines };
}

function parseRewriteExampleBlocks(act: Grade3Activity): RewriteExampleBlock[] | null {
  const raw = act.example_blocks;
  if (!Array.isArray(raw)) return null;
  const blocks: RewriteExampleBlock[] = [];
  for (const b of raw) {
    if (b && typeof b === 'object' && Array.isArray((b as { lines?: unknown }).lines)) {
      const lines = (b as { lines: unknown[] }).lines.map(String);
      if (lines.length) blocks.push({ lines });
    }
  }
  return blocks.length ? blocks : null;
}

function asRewriteItems(raw: unknown): RewriteItemView[] {
  if (!Array.isArray(raw)) return [];
  const out: RewriteItemView[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const q_no = Number(o.q_no);
    if (!Number.isFinite(q_no)) continue;

    if (Array.isArray(o.fragments)) {
      out.push({
        q_no,
        mode: 'fragments_order',
        prompt: typeof o.prompt === 'string' ? o.prompt : '',
        fragments: o.fragments.map(String),
      });
      continue;
    }
    if (typeof o.sentence_a === 'string' && typeof o.sentence_b === 'string') {
      out.push({
        q_no,
        mode: 'merge_sentences',
        task: typeof o.task === 'string' ? o.task : '',
        sentenceA: o.sentence_a,
        sentenceB: o.sentence_b,
      });
      continue;
    }
    out.push({
      q_no,
      mode: 'stem_completion',
      stem: typeof o.stem === 'string' ? o.stem : '',
      completionLines: Array.isArray(o.completion_lines) ? o.completion_lines.map(String) : [],
    });
  }
  return out;
}

function parseConvertExample(act: Grade3Activity): ConvertExample {
  const ex = act.example as { spoken?: unknown; written?: unknown } | undefined;
  return {
    spoken: typeof ex?.spoken === 'string' ? ex.spoken : '',
    written: typeof ex?.written === 'string' ? ex.written : '',
  };
}

function parseConvertChoiceExample(act: Grade3Activity): ConvertChoiceExample {
  const ex = act.example as { spoken?: unknown; written?: unknown } | undefined;
  return {
    spoken: typeof ex?.spoken === 'string' ? ex.spoken : '',
    written: typeof ex?.written === 'string' ? ex.written : '',
  };
}

function asConvertChoiceItems(raw: unknown): ConvertChoiceItem[] {
  if (!Array.isArray(raw)) return [];
  const out: ConvertChoiceItem[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const q_no = Number(o.q_no);
    if (!Number.isFinite(q_no)) continue;
    const spoken = typeof o.spoken === 'string' ? o.spoken : '';
    const options = Array.isArray(o.options) ? o.options.map(String).filter((s) => s.trim().length > 0) : [];
    const correct_answer = typeof o.correct_answer === 'string' ? o.correct_answer.trim() : '';
    if (!spoken.trim() || options.length !== 3 || !correct_answer) continue;
    out.push({ q_no, spoken, options, correct_answer });
  }
  return out.sort((a, b) => a.q_no - b.q_no);
}

function asConvertItems(raw: unknown): ConvertItemView[] {
  if (!Array.isArray(raw)) return [];
  const out: ConvertItemView[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const q_no = Number(o.q_no);
    if (!Number.isFinite(q_no)) continue;

    if (Array.isArray(o.spoken_lines) || Array.isArray(o.written_lines)) {
      const rawSpoken = Array.isArray(o.spoken_lines) ? o.spoken_lines.map(String) : [];
      const rawWritten = Array.isArray(o.written_lines) ? o.written_lines.map(String) : [];
      const n = Math.max(rawSpoken.length, rawWritten.length, 1);
      const spokenLines = Array.from({ length: n }, (_, i) => rawSpoken[i] ?? '');
      const expectedWrittenLines = Array.from({ length: n }, (_, i) => rawWritten[i] ?? '');
      const incomplete = !expectedWrittenLines.some((w) => w.trim());
      out.push({
        q_no,
        kind: 'multi',
        incomplete,
        spokenLines,
        expectedWrittenLines,
      });
      continue;
    }

    const spoken = String(o.spoken ?? '');
    const written = String(o.written ?? '');
    const incomplete = !spoken.trim() && !written.trim();
    out.push({
      q_no,
      kind: 'single',
      incomplete,
      spoken,
      ...(written.trim() ? { expectedWritten: written } : {}),
    });
  }
  return out;
}

function parseCaseSuffixExample(act: Grade3Activity): CaseSuffixFillExample | null {
  const ex = act.example;
  if (!ex || typeof ex !== 'object') return null;
  const o = ex as Record<string, unknown>;
  if (
    typeof o.subject === 'string' &&
    typeof o.answer === 'string' &&
    typeof o.predicate === 'string' &&
    typeof o.hint === 'string'
  ) {
    return { subject: o.subject, answer: o.answer, predicate: o.predicate, hint: o.hint };
  }
  return null;
}

function asCaseSuffixItems(raw: unknown): CaseSuffixFillItem[] {
  if (!Array.isArray(raw)) return [];
  const out: CaseSuffixFillItem[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const o = it as Record<string, unknown>;
    const q_no = Number(o.q_no);
    if (!Number.isFinite(q_no)) continue;
    const subject = typeof o.subject === 'string' ? o.subject : '';
    const predicate = typeof o.predicate === 'string' ? o.predicate : '';
    const hint = typeof o.hint === 'string' ? o.hint : '';
    const options = Array.isArray(o.options) ? o.options.map(String).filter((s) => s.trim().length > 0) : [];
    const correct_answer = typeof o.correct_answer === 'string' ? o.correct_answer.trim() : '';
    if (!subject.trim() || !predicate.trim() || options.length !== 3 || !correct_answer) continue;
    out.push({ q_no, subject, predicate, hint, options, correct_answer });
  }
  return out.sort((a, b) => a.q_no - b.q_no);
}

function renderActivity(
  act: Grade3Activity,
  persistenceId: string,
  onInteracted: () => void,
  onWorkbookStatusChange: (status: WorkbookActivityCompletion) => void
): React.ReactNode {
  const label = typeof act.workbook_label === 'string' ? act.workbook_label : undefined;
  const title = typeof act.title === 'string' ? act.title : 'Activity';
  const instructions = typeof act.instructions === 'string' ? act.instructions : undefined;
  const sourcePage = typeof act.source_page === 'number' ? act.source_page : undefined;

  switch (act.ui_type) {
    case 'qa': {
      const questions = asQuestionArray(act.questions);
      const sup = act.supplementary as { heading?: string; lines?: string[] } | undefined;
      const supplementary =
        sup && Array.isArray(sup.lines) && sup.lines.length > 0
          ? { heading: typeof sup.heading === 'string' ? sup.heading : undefined, lines: sup.lines.map(String) }
          : undefined;
      return (
        <QACard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          questions={questions}
          supplementary={supplementary}
          sourcePage={sourcePage}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'case_suffix_fill': {
      const example = parseCaseSuffixExample(act);
      const csfItems = asCaseSuffixItems(act.items);
      if (!example || csfItems.length === 0) {
        return (
          <ComingSoonCard
            workbookLabel={label}
            title={title}
            instructions={instructions}
            sourcePage={sourcePage}
          />
        );
      }
      return (
        <CaseSuffixFillCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          example={example}
          items={csfItems}
          sourcePage={sourcePage}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'matching': {
      const leftItems = asStringArray(act.left_items);
      const rightItems = asStringArray(act.right_items);
      const pairs = asPairArray(act.pairs);
      return (
        <MatchingCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          leftItems={leftItems}
          rightItems={rightItems}
          pairs={pairs}
          sourcePage={sourcePage}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'fill_blank': {
      const wordBank = asStringArray(act.word_bank);
      const items = asFillItems(act.items);
      const correctAnswers = asStringArray(act.correct_answers);
      return (
        <FillBlankCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          wordBank={wordBank}
          items={items}
          correctAnswers={correctAnswers.length === items.length ? correctAnswers : undefined}
          sourcePage={sourcePage}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'map_label_quiz': {
      const mapImage = typeof act.map_image === 'string' ? act.map_image : '';
      const mapItems = asMapLabelQuizItems(act.items);
      if (!mapImage.trim() || mapItems.length === 0) {
        return (
          <ComingSoonCard
            workbookLabel={label}
            title={title}
            instructions={instructions}
            sourcePage={sourcePage}
          />
        );
      }
      return (
        <MapLabelQuizCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          mapImage={mapImage}
          items={mapItems}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'info': {
      const content = asStringArray(act.content);
      const uiHint = typeof act.ui_hint === 'string' ? act.ui_hint : undefined;
      return (
        <InfoCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          content={content}
          sourcePage={sourcePage}
          uiHint={uiHint}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'rewrite': {
      const example = parseRewriteExample(act);
      const exampleBlocks = parseRewriteExampleBlocks(act);
      const rewriteItems = asRewriteItems(act.items);
      return (
        <RewriteCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          example={example}
          exampleBlocks={exampleBlocks}
          items={rewriteItems}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'convert_words_choice': {
      const convertChoiceExample = parseConvertChoiceExample(act);
      const choiceItems = asConvertChoiceItems(act.items);
      if (!choiceItems.length) {
        return (
          <ComingSoonCard
            workbookLabel={label}
            title={title}
            instructions={instructions}
            sourcePage={sourcePage}
          />
        );
      }
      return (
        <ConvertWordsChoiceCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          example={convertChoiceExample}
          items={choiceItems}
          sourcePage={sourcePage}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'convert_words': {
      const convertExample = parseConvertExample(act);
      const convertItems = asConvertItems(act.items);
      return (
        <ConvertWordsCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          example={convertExample}
          items={convertItems}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'riddle_choice': {
      const prompt = typeof act.prompt === 'string' ? act.prompt : '';
      const riddles = asStringArray(act.riddles);
      const wordBank = asStringArray(act.grid_word_hints);
      const correctAnswers = asStringArray(act.riddle_answers);
      return (
        <RiddleChoiceCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          prompt={prompt}
          riddles={riddles}
          wordBank={wordBank}
          correctAnswers={correctAnswers}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'short_write': {
      const prompt = typeof act.prompt === 'string' ? act.prompt : '';
      const riddles = asStringArray(act.riddles);
      const gridWordHints = asStringArray(act.grid_word_hints);
      return (
        <ShortWriteCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          prompt={prompt}
          riddles={riddles}
          gridWordHints={gridWordHints}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    case 'image_label': {
      const imageLabelItems = asImageLabelItems(act.items);
      return (
        <ImageLabelCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
          items={imageLabelItems}
          persistenceId={persistenceId}
          onInteracted={onInteracted}
          onWorkbookStatusChange={onWorkbookStatusChange}
        />
      );
    }
    default:
      return (
        <ComingSoonCard
          workbookLabel={label}
          title={title}
          instructions={instructions}
          sourcePage={sourcePage}
        />
      );
  }
}

const rank: Record<WorkbookActivityCompletion, number> = {
  not_started: 0,
  in_progress: 1,
  completed: 2,
};

function mergeActivityStatus(
  prev: Record<string, WorkbookActivityCompletion>,
  id: string,
  status: WorkbookActivityCompletion
): Record<string, WorkbookActivityCompletion> {
  const cur = prev[id];
  if (status === 'not_started') {
    if (cur === 'not_started') return prev;
    return { ...prev, [id]: 'not_started' };
  }
  if (cur === 'completed' && status === 'in_progress') {
    return { ...prev, [id]: 'in_progress' };
  }
  if (cur === 'completed') return prev;
  const nextRank = Math.max(rank[cur ?? 'not_started'], rank[status]);
  const next: WorkbookActivityCompletion =
    nextRank === 0 ? 'not_started' : nextRank === 1 ? 'in_progress' : 'completed';
  if (cur === next) return prev;
  return { ...prev, [id]: next };
}

function lessonProgress(lesson: Grade3Lesson, completion: Record<string, WorkbookActivityCompletion>) {
  let done = 0;
  for (const act of lesson.activities) {
    const sub = act.sub_activity != null ? String(act.sub_activity) : 'x';
    const id = `G${workbook.grade}-L${lesson.lesson_no}-A${act.activity_no}-${sub}-${act.ui_type}`;
    if (completion[id] === 'completed') done++;
  }
  return { done, total: lesson.activities.length };
}

function StatusGlyph({ status }: { status: WorkbookActivityCompletion | undefined }) {
  const s = status ?? 'not_started';
  if (s === 'completed') {
    return <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" aria-label="முடிந்தது" />;
  }
  if (s === 'in_progress') {
    return (
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[10px] font-black text-white"
        aria-label="செயலில்"
      >
        ·
      </span>
    );
  }
  return <Circle className="h-5 w-5 shrink-0 text-violet-300 dark:text-violet-600" aria-label="தொடங்கவில்லை" />;
}

export const Grade3AssignmentPage: React.FC = () => {
  const lessons = workbook.lessons;
  const [selectedIndex, setSelectedIndex] = useState(INITIAL_LINEAR_INDEX);
  const [completion, setCompletion] = useState<Record<string, WorkbookActivityCompletion>>(() =>
    readWorkbookJson(TKM_G3_TOC_COMPLETION_KEY, {})
  );
  const [activityScores, setActivityScores] = useState(() => readActivityScores());
  const [expanded, setExpanded] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(lessons.map((l) => [l.lesson_no, true]))
  );
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [resumeBanner, setResumeBanner] = useState(() => {
    if (typeof sessionStorage === 'undefined') return false;
    if (sessionStorage.getItem(RESUME_DISMISS_SESSION_KEY) === '1') return false;
    return INITIAL_LINEAR_INDEX > 0;
  });

  /** After an activity moves to `completed`, show score summary before continuing (skip on first paint / restored state). */
  const [activityScoreModal, setActivityScoreModal] = useState<{
    persistenceId: string;
    title: string;
    lessonNo: number;
    lessonTitle: string;
  } | null>(null);
  const completionBaselineRef = useRef(true);

  useEffect(() => {
    writeWorkbookJson(TKM_G3_TOC_COMPLETION_KEY, completion);
  }, [completion]);

  useEffect(() => {
    const sync = () => setActivityScores(readActivityScores());
    sync();
    window.addEventListener('tkm-g3-scores-updated', sync);
    return () => window.removeEventListener('tkm-g3-scores-updated', sync);
  }, []);

  useEffect(() => {
    writeWorkbookJson(TOC_LAST_KEY, { linearIndex: selectedIndex });
  }, [selectedIndex]);

  useEffect(() => {
    setActivityScoreModal(null);
  }, [selectedIndex]);

  const row = FLAT_ROWS[selectedIndex];
  const act = row?.act;
  const persistenceId = row?.persistenceId ?? '';

  const prevCompletionForModalRef = useRef<Record<string, WorkbookActivityCompletion>>({});
  useEffect(() => {
    if (completionBaselineRef.current) {
      completionBaselineRef.current = false;
      prevCompletionForModalRef.current = { ...completion };
      return;
    }
    const pid = persistenceId;
    if (!pid || !row || !act) {
      prevCompletionForModalRef.current = { ...completion };
      return;
    }
    const prev = prevCompletionForModalRef.current;
    if (completion[pid] === 'completed' && prev[pid] !== 'completed') {
      setActivityScores(readActivityScores());
      setActivityScoreModal({
        persistenceId: pid,
        title: typeof act.title === 'string' && act.title.trim() ? act.title : '—',
        lessonNo: row.lesson.lesson_no,
        lessonTitle: row.lesson.title,
      });
    }
    prevCompletionForModalRef.current = { ...completion };
  }, [completion, persistenceId, row, act]);

  const onActivityStatus = useCallback((id: string, status: WorkbookActivityCompletion) => {
    setCompletion((prev) => mergeActivityStatus(prev, id, status));
  }, []);

  const markEngaged = useCallback(() => {
    if (!persistenceId) return;
    setCompletion((prev) => mergeActivityStatus(prev, persistenceId, 'in_progress'));
  }, [persistenceId]);

  const selectFlatIndex = useCallback((idx: number) => {
    const clamped = Math.min(Math.max(0, idx), FLAT_ROWS.length - 1);
    setSelectedIndex(clamped);
    setMobileTocOpen(false);
    const r = FLAT_ROWS[clamped];
    if (!r) return;
    setCompletion((prev) => {
      const cur = prev[r.persistenceId];
      if (cur === 'completed') return prev;
      if (!cur || cur === 'not_started') return mergeActivityStatus(prev, r.persistenceId, 'in_progress');
      return prev;
    });
  }, []);

  const goNext = useCallback(() => selectFlatIndex(selectedIndex + 1), [selectFlatIndex, selectedIndex]);
  const goPrev = useCallback(() => selectFlatIndex(selectedIndex - 1), [selectFlatIndex, selectedIndex]);

  const canPrev = selectedIndex > 0;
  const canNext = selectedIndex < FLAT_ROWS.length - 1;
  const activityBlocksNextUntilDone =
    (act?.ui_type === 'matching' ||
      act?.ui_type === 'case_suffix_fill' ||
      act?.ui_type === 'convert_words_choice' ||
      act?.ui_type === 'riddle_choice') &&
    completion[persistenceId] !== 'completed';
  const canGoNext = canNext && !activityBlocksNextUntilDone;

  const totalWorkbookActivities = FLAT_ROWS.length;
  const completedWorkbookCount = useMemo(
    () => FLAT_ROWS.filter((r) => completion[r.persistenceId] === 'completed').length,
    [completion]
  );

  const footerHint = useMemo(() => {
    if (!act) return null;
    if (act.ui_type === 'info') {
      return 'இந்தப் பக்கத்தில் “படித்து முடித்தேன்” பொத்தானை அழுத்தவும்.';
    }
    if (act.ui_type === 'matching') {
      return 'இடது/வலது உருப்படிகளைத் தட்டி கோடிணைக்கவும்: சரி பச்சை, தவறு சிவப்பு. அனைத்தும் பச்சையான பின் “அடுத்து” இயங்கும்.';
    }
    if (act.ui_type === 'case_suffix_fill' || act.ui_type === 'convert_words_choice') {
      return 'ஒவ்வொரு வரியிலும் ஒரு விடையைத் தட்டித் தேர்ந்தெடுக்கவும். அனைத்து வரிகளும் தேர்ந்த பின் மதிப்பெண் காட்டப்படும்; “அடுத்து” அனைத்து தேர்வுகளுக்குப் பின் இயங்கும்.';
    }
    if (act.ui_type === 'riddle_choice') {
      return 'ஒவ்வொரு புதிருக்கும் சொல்லைத் தட்டித் தேர்ந்தெடுக்கவும். அனைத்துக்கும் தேர்வு செய்த பின் மதிப்பெண்; “அடுத்து” அப்போது இயங்கும்.';
    }
    if (act.ui_type === 'fill_blank' || act.ui_type === 'convert_words') {
      return 'பதிலைச் சரிபார்க்க அட்டையிலுள்ள “பதில் சரிபார்” பொத்தானைப் பயன்படுத்தவும்.';
    }
    return null;
  }, [act]);

  if (!lessons.length || !FLAT_ROWS.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-violet-200 to-amber-200 p-8 text-lg font-bold text-violet-900 tamil-text">
        பாடங்கள் இல்லை
      </div>
    );
  }

  const tocContent = (
    <nav className="space-y-2" aria-label="பாடப் பட்டியல்">
      {lessons.map((lesson) => {
        const isOpen = expanded[lesson.lesson_no] !== false;
        const { done, total } = lessonProgress(lesson, completion);
        const pct = total ? Math.round((done / total) * 100) : 0;
        const lessonComplete = total > 0 && done === total;
        return (
          <div
            key={lesson.lesson_no}
            className="overflow-hidden rounded-2xl border-4 border-white/90 bg-white/80 shadow-md dark:border-slate-600 dark:bg-slate-800/80"
          >
            <button
              type="button"
              onClick={() =>
                setExpanded((e) => ({
                  ...e,
                  [lesson.lesson_no]: !isOpen,
                }))
              }
              className="flex w-full items-center gap-2 px-3 py-3 text-left"
            >
              <span className="shrink-0 text-violet-600 dark:text-violet-300">
                {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-black uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300">
                    பாடம் {lesson.lesson_no}
                  </span>
                  {lessonComplete ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-black uppercase text-white">
                      <Award className="h-3 w-3" aria-hidden />
                      முழு பாடம்
                    </span>
                  ) : null}
                </div>
                <p className="truncate text-base font-extrabold text-violet-950 tamil-text dark:text-violet-50" lang="ta">
                  {lesson.title}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-violet-100 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="shrink-0 text-xs font-bold text-violet-700 dark:text-violet-200">
                    {done} / {total}
                  </span>
                </div>
              </div>
            </button>
            {isOpen ? (
              <ul className="border-t border-violet-100 px-2 py-2 dark:border-slate-700">
                {lesson.activities.map((a, ai) => {
                  const sub = a.sub_activity != null ? String(a.sub_activity) : 'x';
                  const pid = `G${workbook.grade}-L${lesson.lesson_no}-A${a.activity_no}-${sub}-${a.ui_type}`;
                  const flatIdx = LESSON_ACTIVITY_TO_FLAT_INDEX.get(`${lesson.lesson_no}-${ai}`) ?? 0;
                  const selected = flatIdx === selectedIndex;
                  const st = completion[pid];
                  const sc = activityScores[pid]?.pct;
                  const titleShort =
                    typeof a.title === 'string' && a.title.trim() ? a.title : `${a.ui_type} ${ai + 1}`;
                  return (
                    <li key={`${pid}-${ai}`}>
                      <button
                        type="button"
                        onClick={() => selectFlatIndex(flatIdx)}
                        className={clsx(
                          'mb-1 flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm font-bold transition',
                          selected
                            ? 'bg-gradient-to-r from-amber-200 to-orange-200 text-violet-950 ring-2 ring-amber-400 dark:from-amber-900/50 dark:to-orange-900/40 dark:text-amber-50'
                            : 'text-violet-900 hover:bg-violet-50 dark:text-violet-100 dark:hover:bg-slate-700/80'
                        )}
                      >
                        <StatusGlyph status={st} />
                        <span className="min-w-0 flex-1 truncate tamil-text" lang="ta">
                          {ai + 1}. {titleShort}
                        </span>
                        {typeof sc === 'number' ? (
                          <span
                            className="shrink-0 rounded-md bg-slate-900/90 px-1.5 py-0.5 text-[10px] font-black tabular-nums text-white dark:bg-slate-100 dark:text-slate-900"
                            title="கடைசி மதிப்பெண்"
                          >
                            {sc}%
                          </span>
                        ) : null}
                        <span className="shrink-0 rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-black uppercase text-violet-600 dark:bg-slate-900/80 dark:text-violet-300">
                          {a.ui_type}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-200 via-fuchsia-100 to-amber-200 pb-36 pt-4 dark:from-slate-900 dark:via-violet-950 dark:to-slate-900 sm:pt-6">
      <div className="mx-auto max-w-6xl px-3 sm:px-6">
        <header className="mb-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-bold text-violet-800 shadow-md dark:bg-slate-800/90 dark:text-violet-200">
            <Sparkles className="h-4 w-4 text-amber-500" aria-hidden />
            <span className="tamil-text" lang="ta">
              {workbook.book}
            </span>
            <span className="rounded-full bg-violet-600 px-2 py-0.5 text-xs text-white">Grade {workbook.grade}</span>
          </div>
          <h1 className="mt-3 text-2xl font-black leading-tight text-violet-950 tamil-text sm:text-3xl md:text-4xl dark:text-white" lang="ta">
            பயிற்சி புத்தகம்
          </h1>
          <p className="mt-2 text-sm font-semibold text-violet-800 tamil-text dark:text-violet-200" lang="ta">
            இடது: தேர்ந்தெடுத்த செயல் · வலது: பட்டியல்
          </p>
        </header>

        {resumeBanner ? (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border-4 border-teal-300 bg-teal-50/95 px-4 py-3 text-sm font-bold text-teal-900 shadow-md dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-50 tamil-text">
            <span lang="ta">மீண்டும் வரவேற்கிறோம்! கடைசியாக நின்ற இடத்திலிருந்து தொடர்கிறீர்.</span>
            <button
              type="button"
              onClick={() => {
                try {
                  sessionStorage.setItem(RESUME_DISMISS_SESSION_KEY, '1');
                } catch {
                  /* ignore */
                }
                setResumeBanner(false);
              }}
              className="shrink-0 rounded-xl bg-teal-600 px-3 py-1 text-xs font-black text-white"
              lang="ta"
            >
              சரி
            </button>
          </div>
        ) : null}

        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-white/50 px-3 py-2 dark:bg-slate-800/50 lg:hidden">
          <p className="text-sm font-black text-violet-900 tamil-text dark:text-violet-100" lang="ta">
            மொத்த முன்னேற்றம்: {completedWorkbookCount} / {totalWorkbookActivities}
          </p>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-violet-300 bg-violet-100 px-3 py-2 text-sm font-bold text-violet-900 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100"
            onClick={() => setMobileTocOpen(true)}
          >
            <Menu className="h-5 w-5" aria-hidden />
            <span className="tamil-text" lang="ta">
              செயல்கள்
            </span>
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_minmax(260px,300px)] lg:items-start">
          <section className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border-4 border-white/80 bg-white/70 px-4 py-3 shadow-lg backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/70">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300">
                  {row ? `பாடம் ${row.lesson.lesson_no} · செயல் ${row.activityIdx + 1}` : ''}
                </p>
                <p className="truncate text-lg font-black text-violet-950 tamil-text dark:text-white" lang="ta">
                  {act && typeof act.title === 'string' ? act.title : '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMapOpen((o) => !o)}
                className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl border-2 border-violet-300 bg-violet-100 px-3 py-2 text-xs font-bold text-violet-900 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100"
              >
                <MapIcon className="h-4 w-4" aria-hidden />
                <span className="tamil-text" lang="ta">
                  பக்க வரைபடம்
                </span>
              </button>
            </div>

            {mapOpen && row ? (
              <div className="mb-4 rounded-2xl border-4 border-dashed border-violet-300 bg-white/70 p-4 dark:border-violet-700 dark:bg-slate-800/70">
                <ul className="space-y-2 text-sm font-semibold text-violet-900 tamil-text dark:text-violet-100" lang="ta">
                  {row.lesson.pages.map((p) => (
                    <li key={p.page_no}>
                      <span className="font-black text-fuchsia-600">p.{p.page_no}</span> — {p.note}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <AnimatePresence mode="wait">
              {act && persistenceId ? (
                <motion.div
                  key={persistenceId}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22 }}
                >
                  {renderActivity(act, persistenceId, markEngaged, (status) =>
                    onActivityStatus(persistenceId, status)
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </section>

          <aside className="hidden lg:block lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pl-1">
            <div className="rounded-3xl border-4 border-white/90 bg-gradient-to-b from-white/95 to-violet-50/90 p-3 shadow-xl dark:border-slate-600 dark:from-slate-800/95 dark:to-slate-900/90">
              <p className="mb-3 border-b border-violet-100 pb-2 text-sm font-black text-violet-900 tamil-text dark:border-slate-700 dark:text-violet-100" lang="ta">
                மொத்த முன்னேற்றம்: {completedWorkbookCount} / {totalWorkbookActivities}
              </p>
              <p className="mb-2 flex items-center gap-2 px-1 text-sm font-black text-violet-800 dark:text-violet-200">
                <BookOpen className="h-5 w-5 text-fuchsia-500" aria-hidden />
                <span className="tamil-text" lang="ta">
                  எல்லாச் செயல்களும்
                </span>
              </p>
              {tocContent}
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile TOC drawer */}
      <div
        className={clsx(
          'fixed inset-0 z-40 lg:hidden',
          mobileTocOpen ? 'pointer-events-auto' : 'pointer-events-none'
        )}
        aria-hidden={!mobileTocOpen}
      >
        <button
          type="button"
          className={clsx(
            'absolute inset-0 bg-black/40 transition-opacity',
            mobileTocOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => setMobileTocOpen(false)}
          aria-label="மூடு"
        />
        <div
          className={clsx(
            'absolute left-0 top-0 flex h-full w-[min(100%,20rem)] flex-col border-r-4 border-white/90 bg-gradient-to-b from-white to-violet-50 shadow-2xl transition-transform dark:border-slate-600 dark:from-slate-900 dark:to-slate-900',
            mobileTocOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex items-center justify-between border-b border-violet-100 px-3 py-3 dark:border-slate-700">
            <span className="text-sm font-black text-violet-900 tamil-text dark:text-violet-100" lang="ta">
              செயல் பட்டியல்
            </span>
            <button
              type="button"
              onClick={() => setMobileTocOpen(false)}
              className="rounded-lg p-2 text-violet-700 dark:text-violet-200"
              aria-label="மூடு"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">{tocContent}</div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t-4 border-white/50 bg-white/95 px-3 py-3 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] backdrop-blur-lg dark:border-slate-600 dark:bg-slate-900/95">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-1 sm:justify-center sm:gap-3">
            <button
              type="button"
              disabled={!canPrev}
              onClick={goPrev}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border-4 border-violet-200 bg-violet-50 px-4 py-2 text-sm font-extrabold text-violet-900 shadow-sm transition enabled:active:scale-[0.98] disabled:opacity-40 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-50 tamil-text"
              lang="ta"
            >
              <ChevronLeft className="h-5 w-5 shrink-0" />
              முந்தைய
            </button>
            <button
              type="button"
              disabled={!canGoNext}
              title={
                activityBlocksNextUntilDone
                  ? act?.ui_type === 'matching'
                    ? 'இணைப்புச் செயலை முடிக்கவும்'
                    : 'இந்தச் செயலை முடிக்கவும்'
                  : undefined
              }
              onClick={goNext}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-orange-500 px-4 py-2 text-sm font-black text-white shadow-xl ring-4 ring-amber-200/50 transition enabled:hover:brightness-110 enabled:active:scale-[0.98] disabled:opacity-40 tamil-text"
              lang="ta"
            >
              அடுத்து
              <ChevronRight className="h-5 w-5 shrink-0" />
            </button>
          </div>
        </div>
        {/* {footerHint ? (
          <p className="mx-auto mt-2 max-w-6xl text-center text-[11px] font-semibold text-violet-700 tamil-text dark:text-violet-300/90" lang="ta">
            {footerHint}
          </p>
        ) : null}
        <p className="mx-auto mt-1 max-w-6xl text-center text-[11px] font-semibold text-violet-600/80 tamil-text dark:text-violet-400/80" lang="ta">
          <BookOpen className="mr-1 inline h-3 w-3 align-middle" aria-hidden />
          விடைகள் இந்தச் சாதனத்தில் மட்டும்; சேவையகம் இல்லை
        </p> */}
      </div>

      {activityScoreModal ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="g3-activity-score-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            aria-label="மூடு"
            onClick={() => setActivityScoreModal(null)}
          />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl border-4 border-white bg-gradient-to-b from-white via-violet-50/90 to-amber-50/80 p-6 shadow-2xl dark:border-slate-600 dark:from-slate-800 dark:via-slate-900 dark:to-slate-900">
            <div className="text-center">
              <PartyPopper className="mx-auto h-14 w-14 text-amber-500 drop-shadow-md dark:text-amber-400" aria-hidden />
              <h2
                id="g3-activity-score-modal-title"
                className="mt-3 text-2xl font-black text-violet-950 tamil-text dark:text-white"
                lang="ta"
              >
                செயல் முடிந்தது!
              </h2>
              <p
                className="mt-2 text-sm font-bold leading-snug text-violet-800 tamil-text dark:text-violet-200"
                lang="ta"
              >
                பாடம் {activityScoreModal.lessonNo}: {activityScoreModal.lessonTitle}
              </p>
              <p className="mt-2 text-lg font-extrabold text-violet-900 tamil-text dark:text-violet-50" lang="ta">
                {activityScoreModal.title}
              </p>

              <div className="mt-6 rounded-2xl border-4 border-amber-300/80 bg-gradient-to-br from-amber-50 to-orange-50 p-5 dark:border-amber-700/60 dark:from-amber-950/50 dark:to-orange-950/40">
                <p className="text-xs font-black uppercase tracking-wide text-amber-900 dark:text-amber-200" lang="ta">
                  இந்தச் செயலின் மொத்த மதிப்பெண்
                </p>
                <p className="mt-2 text-5xl font-black tabular-nums text-amber-700 dark:text-amber-300">
                  {typeof activityScores[activityScoreModal.persistenceId]?.pct === 'number'
                    ? `${activityScores[activityScoreModal.persistenceId]!.pct}%`
                    : '—'}
                </p>
                {typeof activityScores[activityScoreModal.persistenceId]?.pct !== 'number' ? (
                  <p className="mt-2 text-xs font-semibold leading-relaxed text-amber-900/85 tamil-text dark:text-amber-200/85" lang="ta">
                    இச்செயலுக்கு சதவீத மதிப்பெண் இல்லை (படித்தல் அல்லது எழுத்து வடிவம்).
                  </p>
                ) : null}
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                {selectedIndex < FLAT_ROWS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => {
                      const next = selectedIndex + 1;
                      setActivityScoreModal(null);
                      selectFlatIndex(next);
                    }}
                    className="inline-flex min-h-[52px] min-w-[10rem] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-orange-500 px-6 py-3 text-base font-black text-white shadow-lg ring-2 ring-amber-200/60 transition hover:brightness-110 tamil-text"
                    lang="ta"
                  >
                    அடுத்து செயல்
                    <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                  </button>
                ) : (
                  <p className="text-center text-sm font-bold text-violet-700 tamil-text dark:text-violet-300" lang="ta">
                    இது இந்தப் புத்தகத்தின் கடைசிச் செயல்.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setActivityScoreModal(null)}
                  className="inline-flex min-h-[52px] items-center justify-center rounded-2xl border-4 border-violet-300 bg-white px-6 py-3 text-base font-extrabold text-violet-900 shadow-sm transition hover:bg-violet-50 dark:border-violet-600 dark:bg-slate-800 dark:text-violet-100 dark:hover:bg-slate-700 tamil-text"
                  lang="ta"
                >
                  இங்கேயே இரு
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Grade3AssignmentPage;
